import { BASEURL } from "../BaseUrl";

/**
 * Fetches a list of clients with optional filters and pagination.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The parameters to filter and paginate the client list.
 * @param {string} data.user_id - The ID of the user requesting the client list.
 * @param {string} data.domain - The domain to filter clients by.
 * @param {string} data.domain_id - The ID of the domain to filter clients by.
 * @param {string} data.search - The search term to filter clients by name or other attributes.
 * @param {string} data.searchParam - The parameter to search by (e.g., name, email).
 * @param {number} data.pageNumber - The page number for pagination.
 * @param {number} data.pageSize - The number of clients per page for pagination.
 * @param {string} data.status - The status of the clients to filter by.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const getAllClients = async (axios, data) => {
  return await axios.get(`${BASEURL}/client/get-client`, {
    params: {
      user_id: data.user_id,
      domain: data.domain,
      domain_id: data.domain_id,
      search: data.search,
      searchParam: data.searchParam,
      pageNumber: data.pageNumber,
      pageSize: data.pageSize,
      status: data.status,
    },
  });
};

/**
 * Updates the status of a specific client.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {Object} data - The data containing the client ID and new status.
 * @param {string} data.client_id - The ID of the client whose status is to be updated.
 * @param {string} data.status - The new status to set for the client.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const clientStatusChanged = async (axios, data) => {
  return await axios.put(`${BASEURL}/client/client-status-changed`, null, {
    params: {
      client_id: data.client_id,
      status: data.status,
    },
  });
};

/**
 * Deletes a specific client by ID.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} client_id - The ID of the client to be deleted.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const deleteClient = async (axios, client_id) => {
  return await axios.delete(`${BASEURL}/client/delete-client`, {
    params: { client_id },
  });
};

/**
 * Creates a new client with the provided details.
 * @param {Object} axios - The Axios instance to use for the request.
 * @param {string} userId - The ID of the user creating the client.
 * @param {string} username - The username of the user creating the client.
 * @param {string} selectedDomain - The ID of the domain the client belongs to.
 * @param {string} clientName - The name of the new client.
 * @returns {Promise} - A Promise that resolves with the response from the API.
 */
const createClient = async (
  axios,
  userId,
  username,
  selectedDomain,
  clientName
) => {
  return await axios.post(`${BASEURL}/client/add-client`, {
    clientName: clientName,
    userId: userId,
    userName: username,
    domainId: selectedDomain,
    clientCode: clientName,
  });
};

export const ClientApi = {
  getAllClients,
  deleteClient,
  createClient,
  clientStatusChanged,
};
